import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("This invitation link is incomplete.");
      setStatus("error");
      return;
    }
    api
      .get(`/team-access/public/invitations/${token}`)
      .then(({ data }) => {
        setInvite(data);
        setStatus("ready");
      })
      .catch((requestError) => {
        setError(apiErr(requestError));
        setStatus("error");
      });
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (password.length < 12) {
      setError("Choose a password with at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setStatus("saving");
    setError("");
    try {
      const { data } = await api.post("/team-access/public/accept-invitation", {
        token,
        password,
      });
      const result = await login(data.email, password);
      if (!result.ok) throw new Error(result.error);
      setStatus("complete");
      setTimeout(() => navigate("/"), 900);
    } catch (requestError) {
      setError(apiErr(requestError, "We could not activate this invitation."));
      setStatus("ready");
    }
  };

  return (
    <main
      className={
        "relative grid min-h-screen place-items-center overflow-hidden bg-[#eaf3f1] " +
        "px-4 py-10"
      }
      data-testid="accept-invitation-page"
    >
      <div className="absolute inset-x-0 top-0 h-3 bg-[#255058]" />
      <Card className="w-full max-w-lg border-[#c8dcd7] p-6 shadow-xl sm:p-9">
        <div className="mb-7 flex items-center gap-3">
          <div
            className={
              "grid h-11 w-11 place-items-center rounded-lg bg-[#255058] font-display " +
              "font-bold text-white"
            }
          >
            N
          </div>
          <div>
            <div className="font-display text-xl font-bold">Nexreq ATS</div>
            <div className="text-xs text-muted-foreground">
              Secure account invitation
            </div>
          </div>
        </div>

        {status === "loading" && (
          <Loader2
            className="mx-auto h-7 w-7 animate-spin text-[#255058]"
            data-testid="accept-invitation-loading"
          />
        )}
        {status === "error" && <InvitationError error={error} />}
        {(status === "ready" || status === "saving") && invite && (
          <form
            onSubmit={submit}
            className="space-y-5"
            data-testid="accept-invitation-form"
          >
            <div>
              <h1 className="font-display text-2xl font-bold">
                Welcome, {invite.name}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Activate your {invite.role_label} access for {invite.email}.
              </p>
              {invite.team_name && (
                <div className="mt-2 text-xs text-[#255058]">
                  Team: {invite.team_name}
                </div>
              )}
            </div>
            <div
              className={
                "rounded-md border border-[#c8dcd7] bg-[#f7fbfa] p-3 text-sm " +
                "text-[#255058]"
              }
            >
              <ShieldCheck className="mr-1.5 inline h-4 w-4" /> Your invitation
              is one-time and expires automatically.
            </div>
            <div>
              <Label htmlFor="invite-password">Create password</Label>
              <Input
                id="invite-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                data-testid="accept-invitation-password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="invite-password-confirm">Confirm password</Label>
              <Input
                id="invite-password-confirm"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                data-testid="accept-invitation-confirm-password"
                autoComplete="new-password"
              />
            </div>
            {error && (
              <p
                className="text-sm text-red-600"
                data-testid="accept-invitation-error"
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={status === "saving"}
              data-testid="accept-invitation-submit-button"
              className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              {status === "saving" ? "Activating account…" : "Activate account"}
            </Button>
          </form>
        )}
        {status === "complete" && (
          <div
            className="py-6 text-center"
            data-testid="accept-invitation-success"
          >
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
            <h1 className="font-display text-2xl font-bold">
              Account activated
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Opening your Nexreq workspace…
            </p>
          </div>
        )}
      </Card>
    </main>
  );
}

function InvitationError({ error }) {
  return (
    <div className="py-5 text-center" data-testid="accept-invitation-invalid">
      <h1 className="font-display text-2xl font-bold">
        Invitation unavailable
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      <Button
        variant="outline"
        onClick={() => window.location.assign("/login")}
        data-testid="accept-invitation-login-button"
        className="mt-5"
      >
        Go to sign in
      </Button>
    </div>
  );
}
